import {
  CSSDimension,
  Integer,
  Box,
  HStack,
  Scroll,
  Show,
  Space,
  VStack,
  ValueByDevice,
  useResponsivePropValue,
} from 'platform/foundation';

import {useState, forwardRef, useImperativeHandle} from 'react';

import {concat, equals} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {Action, Actions} from '../Actions/Actions';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {WizardStep} from './components/WizardStep';
import {useWizardKeyboardNavigation} from './hooks/useWizardKeyboardNavigation';
import {getFirstEnabledStepPosition} from './utils/getFirstEnabledStepPosition';
import {getNextStep} from './utils/getNextStep';
import {getPreviousStep} from './utils/getPreviousStep';
import {WizardStepConfig} from './WizardStepConfig';

export interface WizardProps extends TestIdProps {
  steps: WizardStepConfig[];
  width?: CSSDimension | Integer;
  height?: CSSDimension | Integer;
  minHeight?: CSSDimension | Integer;
  actions?: Action[];
  orientation?: 'vertical' | 'horizontal' | ValueByDevice<'vertical' | 'horizontal'>;
  onStepChange?: (step: number) => void;
  onKeyDownCapture?: React.KeyboardEventHandler<HTMLDivElement>;
}

export interface WizardRef {
  setCurrentStep: (step: number) => void;
  getCurrentStep: () => number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

export const Wizard = forwardRef<WizardRef, WizardProps>((props, ref) => {
  const orientation = useResponsivePropValue(props.orientation) ?? 'vertical';
  const t = useTranslationContext();

  const [currentStep, setCurrentStep] = useState(
    () => getFirstEnabledStepPosition(props.steps) ?? 0
  );

  // Expose step control functions via ref
  useImperativeHandle(
    ref,
    () => ({
      setCurrentStep: (step: number) => {
        if (step >= 0 && step < props.steps.length) {
          setCurrentStep(step);
        }
      },
      getCurrentStep: () => currentStep,
      goToNextStep: () => {
        const nextStep = getNextStep(props.steps, currentStep);
        if (nextStep !== null) {
          setCurrentStep(nextStep);
        }
      },
      goToPreviousStep: () => {
        const previousStep = getPreviousStep(props.steps, currentStep);
        if (previousStep !== null) {
          setCurrentStep(previousStep);
        }
      },
    }),
    [currentStep, props.steps]
  );

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    props.onStepChange?.(step);
  };

  const {focusedStep, handleKeyDown, handleFocusIn} = useWizardKeyboardNavigation({
    steps: props.steps,
    currentStep,
    onStepChange: handleStepChange,
  });

  const previousStep = getPreviousStep(props.steps, currentStep);
  const nextStep = getNextStep(props.steps, currentStep);

  const currentActions: Action[] = buildArray<Action>()
    .when(isNotNil(previousStep), {
      type: 'button',
      variant: 'secondary',
      title: t('general.actions.back'),
      'data-testid': suffixTestId('backButton', props),
      onClick: () => handleStepChange(previousStep!),
    })
    .when(isNotNil(nextStep), {
      type: 'button',
      variant: 'secondary',
      title: t('general.actions.continue'),
      'data-testid': suffixTestId('continueButton', props),
      onClick: () => handleStepChange(nextStep!),
    });

  const currentConfig = props.steps.at(currentStep);
  const stepDrivenActions = concat(currentActions, currentConfig?.actions ?? []);

  const showActionsBar = isNotNilOrEmpty(props.actions) && isNotNilOrEmpty(stepDrivenActions);

  return (
    <div onKeyDownCapture={props.onKeyDownCapture}>
      <Box
        width={props.width}
        height={props.height}
        minHeight={props.minHeight}
        data-testid={props['data-testid']}
      >
        {orientation === 'horizontal' ? (
          <VStack height="100%" minHeight={props.minHeight}>
            <div
              role="tablist"
              aria-orientation="horizontal"
              onKeyDown={handleKeyDown}
              onFocus={handleFocusIn}
            >
              <Box backgroundColor="palettes.neutral.20.100" width="100%">
                <HStack align="center" width="100%">
                  {props.steps.map((step, index) => (
                    <Box flex={1} minWidth={0} key={`wizardStep-item-${index}`}>
                      <WizardStep
                        {...step}
                        position={index}
                        borderPosition="top"
                        onClick={handleStepChange}
                        isCurrent={equals(currentStep, index)}
                        isFocused={equals(focusedStep, index)}
                        data-testid={suffixTestId(`wizardStep-${index}`, props)}
                      />
                    </Box>
                  ))}
                </HStack>
              </Box>
            </div>
            <Box
              flex={1}
              height="100%"
              role="tabpanel"
              id={`wizard-tabpanel-${currentStep}`}
              aria-labelledby={`wizard-tab-${currentStep}`}
            >
              <VStack justify="space-between" minHeight="100%" spacing={6}>
                <Scroll auto flex={1} showGutter>
                  <Box
                    key={`wizardContent-${currentStep}`}
                    data-testid={suffixTestId('wizardContent', props)}
                    height="100%"
                  >
                    {currentConfig?.content}
                  </Box>
                </Scroll>
                <Show when={showActionsBar}>
                  <Box paddingHorizontal={6}>
                    <HStack justify="space-between">
                      <Actions
                        size="default"
                        actions={props.actions}
                        data-testid={suffixTestId('customActions', props)}
                      />
                      <Space fillAvailable />
                      <Actions
                        size="default"
                        actions={stepDrivenActions}
                        data-testid={suffixTestId('stepDrivenActions', props)}
                      />
                    </HStack>
                  </Box>
                </Show>
              </VStack>
            </Box>
          </VStack>
        ) : (
          <HStack height="100%" minHeight={props.minHeight}>
            <Scroll width={70} auto>
              <div
                role="tablist"
                aria-orientation="vertical"
                onKeyDown={handleKeyDown}
                onFocus={handleFocusIn}
              >
                <Box backgroundColor="palettes.neutral.20.100" minHeight={props.minHeight}>
                  <VStack>
                    {props.steps.map((step, index) => (
                      <WizardStep
                        {...step}
                        position={index}
                        onClick={handleStepChange}
                        isCurrent={equals(currentStep, index)}
                        isFocused={equals(focusedStep, index)}
                        key={`wizardStep-${index}`}
                        data-testid={suffixTestId(`wizardStep-${index}`, props)}
                      />
                    ))}
                  </VStack>
                </Box>
              </div>
            </Scroll>
            <Box
              flex={1}
              paddingVertical={6}
              backgroundColor="text.white"
              height="100%"
              role="tabpanel"
              id={`wizard-tabpanel-${currentStep}`}
              aria-labelledby={`wizard-tab-${currentStep}`}
            >
              <VStack justify="space-between" height="100%" spacing={6}>
                <Scroll auto flex={1}>
                  <Box
                    paddingHorizontal={6}
                    key={`wizardContent-${currentStep}`}
                    data-testid={suffixTestId('wizardContent', props)}
                  >
                    {currentConfig?.content}
                  </Box>
                </Scroll>
                <Show when={showActionsBar}>
                  <Box paddingHorizontal={6}>
                    <HStack justify="space-between">
                      <Actions
                        size="default"
                        actions={props.actions}
                        data-testid={suffixTestId('customActions', props)}
                      />
                      <Space fillAvailable />
                      <Actions
                        size="default"
                        actions={stepDrivenActions}
                        data-testid={suffixTestId('stepDrivenActions', props)}
                      />
                    </HStack>
                  </Box>
                </Show>
              </VStack>
            </Box>
          </HStack>
        )}
      </Box>
    </div>
  );
});
